import {
  ManualDefinitionTableDataValue,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { Options } from '@asset-metrix/portal-ui';
import { JsonSchema } from '@sagold/react-json-editor';
import { omit, remove, uniq } from 'lodash';

export const MULTI_VALUE_PLACEHOLDER = 'Multiple values *';

const isPropertyTypeDate = (property: JsonSchema) => {
  if (property.format && property.format.includes('date')) {
    return true;
  }
  return false;
};

const isPropertyTypeString = (property: JsonSchema) => {
  if (
    property.type?.length &&
    (property.type.includes('string') || property.type.includes('number'))
  ) {
    return true;
  }

  return property.type === 'string';
};

const getRowUniqueValues = (
  values: ManualDefinitionTableDataValue[],
  schema: JsonSchema
): ManualDefinitionTableDataValue[] => {
  if (isPropertyTypeString(schema)) {
    return uniq(values);
  }

  if (schema.type === 'array') {
    /**
     * Multi value comparison steps
     * 1 - Stringify all the object values to compare
     * 2 - Eliminate duplicates using the uniq method
     * 3 - Parse it back to JSON to get the original object
     * E.g :  propertyValueSelected = [{ id: '1' }, { id: '2' }, { id: '3' }, { id: '1' }]
     *        returned value = [{ id: '1' }, { id: '2' }, { id: '3' }]
     *
     */
    return uniq(
      values.map((value) =>
        typeof value === 'object' ? JSON.stringify(value) : value
      )
    ).map((value) => {
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          // Return plain value below
        }
      }

      return value;
    });
  }

  return [];
};

export function addMultiValueAsOptions(
  schema: JsonSchema,
  filterData: { [p: string]: Options },
  initialData: {
    data: TableRowDto['data'];
  }[]
) {
  // For all the filters add 'Multiple values *' in dropdown options if required
  const { properties } = schema;
  if (properties) {
    for (const key in properties) {
      const propertySchema = properties[key] as JsonSchema;
      const dataProperty = propertySchema?.conditionalFilter?.dataProperty;
      // Get all unique row value for each schema property
      const rowValues = initialData.map((row) => {
        return row.data[key];
      });
      const uniqueRowValues = getRowUniqueValues(rowValues, propertySchema);
      /**
       * Fetch labels from multi-level data to check if labels exist in dropdown listing
       */
      const multiValuePropertyLabels = [];
      for (const item of uniqueRowValues) {
        const labels = Array.isArray(item)
          ? item.map((subItem) => {
              if (typeof subItem === 'object' && subItem?.name) {
                return subItem.name;
              }
              return subItem;
            })
          : [typeof item === 'object' && item?.name ? item.name : item];
        multiValuePropertyLabels.push(...uniq(labels));
      }

      if (
        multiValuePropertyLabels.every((item) => {
          return (
            filterData[dataProperty]?.labels.includes(item as string) ||
            item === ''
          );
        }) &&
        uniqueRowValues.length > 1
      ) {
        const propertyValues = filterData[dataProperty];
        // Prevent duplicate insertion
        if (
          !propertyValues?.labels.find(
            (item) => item === MULTI_VALUE_PLACEHOLDER
          )
        ) {
          propertyValues?.labels?.unshift(MULTI_VALUE_PLACEHOLDER);
          propertyValues?.values?.unshift(MULTI_VALUE_PLACEHOLDER);
        }
      } else {
        // Remove 'Multi Values *' option if already present
        const propertyValues = filterData[dataProperty];
        remove(propertyValues?.labels, function (item) {
          return item === MULTI_VALUE_PLACEHOLDER;
        });
        remove(propertyValues?.values, function (item) {
          return item === MULTI_VALUE_PLACEHOLDER;
        });
      }
    }
  }
}

export function addMultiValueAsDataValue(
  schema: JsonSchema,
  initialData: {
    data: TableRowDto['data'];
  }[]
): { updatedData: Record<string, unknown>; updatedSchema: JsonSchema } {
  const updatedData: Record<string, unknown> = {};
  // For all the filters add 'Multiple values *' in dropdown options if required
  const { properties } = schema;
  const updatedSchema = structuredClone(schema);

  if (properties) {
    for (const key in properties) {
      const propertySchema = properties[key] as JsonSchema;
      // Get all unique row value for each schema property
      const rowValues = initialData.map((row) => {
        return row.data[key];
      });
      const uniqueRowValues = getRowUniqueValues(rowValues, propertySchema);

      if (uniqueRowValues.length === 1) {
        updatedData[key] = uniqueRowValues[0];
      } else if (uniqueRowValues.length > 1) {
        // For string and date types, add 'Multiple values *' placeholders
        if (
          isPropertyTypeDate(propertySchema) ||
          isPropertyTypeString(propertySchema)
        ) {
          if (
            updatedSchema.properties &&
            typeof updatedSchema.properties[key] === 'object'
          ) {
            const previousOptions = updatedSchema.properties[key].options;
            // Keep display/behavior options (readOnly, hidden, factor, widget, …).
            // Drop date bounds (literal and ref) — they block bulk edits with placeholders.
            updatedSchema.properties[key].options = {
              ...omit(previousOptions, [
                'minDate',
                'maxDate',
                'minDateRef',
                'maxDateRef',
              ]),
              placeholder: MULTI_VALUE_PLACEHOLDER,
              required: false,
            };
            updatedSchema.properties[key].minItems = 0;
            updatedSchema.properties[key].minLength = 0;
          }
        } else {
          // resolve dataPointer e.g. "document_type.id" | "document_type" | undefined
          const fieldFragmentIdentifier =
            typeof propertySchema.options?.dataPointer === 'string'
              ? propertySchema.options?.dataPointer?.split('.')?.[1]
              : undefined;

          updatedData[key] = fieldFragmentIdentifier
            ? [
                {
                  [fieldFragmentIdentifier]: MULTI_VALUE_PLACEHOLDER,
                  name: MULTI_VALUE_PLACEHOLDER,
                },
              ]
            : [MULTI_VALUE_PLACEHOLDER];
        }
      }
    }
  }

  return {
    updatedData,
    updatedSchema,
  };
}
